import { z } from "zod";
import type { BillingPlanCode, LifetimePlanCode } from "./billing";

/**
 * The App Store side of billing: what the Apple apps sell, mapped onto the plan codes every store shares, and what the
 * apps send the server. Purchases land in `entitlements` with `source: "apple"`, next to Paddle and Google Play rows,
 * and `user.plan` stays the highest entitled tier (docs/research/2026-10-one-account-plan.md §4).
 *
 * The server never takes the app's word for a purchase: it verifies every signed transaction (JWS) itself against
 * Apple's root certificate (apps/api/src/billing/app-store.ts). Mirrors `apps/apple/Bridewell/Store/Products.swift`
 * and `Store/Grandfather.swift`; a change there is a change here, in the same piece of work.
 */
export const APP_STORE = {
  /** The bundle id every Apple platform shares (Universal Purchase): iPhone, iPad, Mac, Vision. */
  bundleId: "ataman.bridewell",
  /** The app's Apple id: production transactions carry it as `appAppleId`. */
  appAppleId: 6770368982,
  products: {
    premiumMonthly: "ataman.bridewell.premium.monthly",
    premiumAnnual: "ataman.bridewell.premium.annual",
    /** The one-time unlock (non-consumable): Premium for life. */
    premiumLifetime: "ataman.bridewell.premium.lifetime",
  },
  /**
   * Installs from before the first paid release (2.0.6, 2026-07-14) are Premium for life. Frozen at that release:
   * iOS, iPadOS and visionOS report the original *build number* (below 100), macOS the original *marketing version*
   * (below 2.0.6). Only production purchases count — the sandbox reports "1.0" for everyone.
   */
  grandfather: { buildCutoff: 100, macVersionCutoff: [2, 0, 6] as const },
  /** Where a person manages App Store subscriptions (Apple's own page; no purchase happens there). */
  manageSubscriptionsUrl: "https://apps.apple.com/account/subscriptions",
} as const;

/** The plan code an App Store product grants; null for anything that unlocks nothing (tips) or that Bridewell does not sell. */
export function planCodeForAppStore(productId: string | null | undefined): BillingPlanCode | LifetimePlanCode | null {
  switch (productId) {
    case APP_STORE.products.premiumMonthly:
      return "premium_monthly";
    case APP_STORE.products.premiumAnnual:
      return "premium_annual";
    case APP_STORE.products.premiumLifetime:
      return "premium_lifetime";
    default:
      return null;
  }
}

/**
 * Whether a signed `AppTransaction` belongs to the grandfathered cohort — the same verdict as `Grandfather.verdict` in
 * the Apple app. `originalPlatform` (iOS 18.4+) says which rule applies; without it the build-number rule is used,
 * which is also what an iPhone reports.
 */
export function isGrandfatheredInstall(t: { environment: string | null | undefined; originalApplicationVersion: string | null | undefined; originalPlatform?: string | null }): boolean {
  if (t.environment !== "Production" || !t.originalApplicationVersion) return false;
  const original = t.originalApplicationVersion.trim();
  if (t.originalPlatform === "macOS") return versionBelow(original, APP_STORE.grandfather.macVersionCutoff);
  // Build numbers like "19" have no dots; a dotted one ("100.1") compares by its leading component.
  const build = Number.parseInt(original.split(".")[0] ?? "", 10);
  return Number.isFinite(build) && build < APP_STORE.grandfather.buildCutoff;
}

function versionBelow(version: string, cutoff: readonly number[]): boolean {
  const parts = version.split(".").map((p) => Number.parseInt(p, 10));
  if (parts.some((p) => !Number.isFinite(p))) return false;
  for (let i = 0; i < cutoff.length; i++) {
    const a = parts[i] ?? 0;
    const b = cutoff[i]!;
    if (a !== b) return a < b;
  }
  return false;
}

/* ───────────────────────── Wire types ───────────────────────── */

/** A compact JWS as StoreKit 2 hands it out (`jwsRepresentation`): three base64url parts, the header carrying `x5c`. */
const jws = z.string().min(100).max(16_000).regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/);

/**
 * `POST /me/billing/apple/claim` — what StoreKit holds on this device, exactly as Apple signed it: every
 * `Transaction.currentEntitlements` entry, the subscriptions' renewal info and the `AppTransaction` (which decides the
 * grandfathered cohort). Sent after signing in, after a purchase and on "Restore purchases".
 */
export const claimAppStorePurchasesSchema = z.object({
  // Bridewell sells three products in one subscription group: a handful of JWS at most, well inside the API's 64 KB
  // body limit for /me/* (each carries its ~4 KB certificate chain).
  transactions: z.array(jws).max(5),
  renewals: z.array(jws).max(5).default([]),
  appTransaction: jws.optional(),
});
export type ClaimAppStorePurchases = z.infer<typeof claimAppStorePurchasesSchema>;

/** `GET /me/billing/apple` */
export interface AppStoreBillingStatusDTO {
  /** False while the server cannot verify App Store purchases; the app then claims nothing. */
  enabled: boolean;
  bundleId: string;
  /**
   * StoreKit's `appAccountToken` for purchases this account makes: the Bridewell user id (a UUID). Apple copies it
   * into every renewal, so the server's notifications know whose purchase it is.
   */
  appAccountToken: string;
}

/** `meta` of the claim answer: what the server did with what the app sent. */
export interface AppStoreClaimResultDTO {
  /** Purchases now recorded for this account (new or already known). */
  claimed: number;
  /** Purchases that belong to another Bridewell account and were left alone. */
  conflicts: number;
  /** Whether the account now has a paid plan (from any store). */
  granted: boolean;
}
